import { toast } from 'react-toastify';
import store from '../store';
import authSlice from '../store/slices/auth';
import axiosService, { refreshAccessToken } from './axios';

export type PrivilegeStatus = {
    isAdmin: boolean;
    isPartner: boolean;
};

function isAuthDenial(error: unknown): boolean {
    const status = (error as { response?: { status?: number } })?.response?.status;
    return status === 401 || status === 403;
}

/**
 * Verifies the current user's admin/partner claims against the database and
 * self-heals both directions:
 *  - Revokes (fails closed) any claim that no longer verifies, immediately.
 *  - Picks up newly granted claims via a fresh /user/:id fetch + token refresh,
 *    so users don't have to log out/in to see new access.
 *
 * `Layout`, `useAdminAccess`, and `usePartnerAccess` all call this for the same
 * user around the same time (e.g. Layout wraps every admin/partner page, and
 * React.StrictMode double-invokes effects in development). Without
 * deduplication, those concurrent callers would each independently verify,
 * dispatch, and refresh the token, racing each other and causing the
 * admin/partner flags to flap. Concurrent callers instead share a single
 * in-flight run and its result.
 */
let inFlight: Promise<PrivilegeStatus> | null = null;

export function syncPrivileges(userId: string, isAdminInStore: boolean, isPartnerInStore: boolean): Promise<PrivilegeStatus> {
    if (inFlight) {
        return inFlight;
    }

    inFlight = performSync(userId, isAdminInStore, isPartnerInStore).finally(() => {
        inFlight = null;
    });

    return inFlight;
}

async function performSync(userId: string, isAdminInStore: boolean, isPartnerInStore: boolean): Promise<PrivilegeStatus> {
    let isAdmin = isAdminInStore;
    let isPartner = isPartnerInStore;

    if (isPartnerInStore) {
        try {
            const response = await axiosService.post('/partner/verify-token');
            isPartner = response.data?.isValid === true;
        } catch (error) {
            // Fail closed only on auth denials. Transient network / rate-limit
            // errors must not bounce partners out of the portal.
            if (isAuthDenial(error)) {
                isPartner = false;
            }
        }
    }

    if (isAdminInStore) {
        try {
            const response = await axiosService.post('/admin/verify-token');
            isAdmin = response.data?.isValid === true;
        } catch (error) {
            if (isAuthDenial(error)) {
                isAdmin = false;
            }
        }
    }

    const partnerRevoked = isPartnerInStore && !isPartner;
    const adminRevoked = isAdminInStore && !isAdmin;

    if (partnerRevoked || adminRevoked) {
        const currentUser = store.getState().auth.user;
        if (currentUser) {
            store.dispatch(authSlice.actions.setUser({
                ...currentUser,
                ...(partnerRevoked ? { isPartner: false } : {}),
                ...(adminRevoked ? { isAdmin: false } : {}),
            }));
        }

        if (adminRevoked) {
            toast.error('Your admin privileges have been revoked', {
                position: 'top-right',
                autoClose: 5000
            });
        }
        if (partnerRevoked) {
            toast.error('Your partner privileges have been revoked', {
                position: 'top-right',
                autoClose: 5000
            });
        }
    }

    // If either privilege isn't already confirmed, refresh user data. This
    // catches cases where access was granted after login, without requiring
    // the user to log out and back in.
    if (!isPartner || !isAdmin) {
        try {
            const userResponse = await axiosService.get(`/user/${userId}`);
            if (userResponse.data) {
                const partnerGranted = !isPartnerInStore && userResponse.data.isPartner === true;
                const adminGranted = !isAdminInStore && userResponse.data.isAdmin === true;

                store.dispatch(authSlice.actions.setUser(userResponse.data));
                isAdmin = userResponse.data.isAdmin === true;
                isPartner = userResponse.data.isPartner === true;

                if (partnerGranted || adminGranted) {
                    try {
                        // Reissue the token so it carries the newly granted claim(s)
                        await refreshAccessToken();
                    } catch (refreshError) {
                        console.error('Failed to refresh token after privilege grant', refreshError);
                        toast.error(
                            'Access was granted but your session could not be refreshed. Please log out and log back in.',
                            { position: 'top-right', autoClose: 8000 }
                        );
                        // Roll back the store flag that APIs cannot honor yet
                        // so the UI does not show partner/admin nav that 403s.
                        const currentUser = store.getState().auth.user;
                        if (currentUser && partnerGranted) {
                            store.dispatch(authSlice.actions.setUser({
                                ...currentUser,
                                isPartner: false,
                            }));
                            isPartner = false;
                        }
                        if (currentUser && adminGranted) {
                            const latest = store.getState().auth.user;
                            if (latest) {
                                store.dispatch(authSlice.actions.setUser({
                                    ...latest,
                                    isAdmin: false,
                                }));
                            }
                            isAdmin = false;
                        }
                    }
                }
            }
        } catch (fetchError) {
            console.error('Failed to sync user privileges', fetchError);
        }
    }

    return { isAdmin, isPartner };
}
